/**
 * Quill AI Administration — section shell
 * Mirrors the pattern of ../content/ContentManagement.tsx: its own <Routes>
 * mounted at /dashboard/quill/*, wrapped in the health provider so every
 * child page can read backend status via useQuillHealth() without its own
 * fetch.
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import EmptyState from '../common/EmptyState';
import { QuillHealthProvider, useQuillHealth } from './QuillHealthContext';
import AIModelsPage from './models/AIModelsPage';
import PipelineModelsPage from './pipeline-models/PipelineModelsPage';
import AIStylesPage from './ai-styles/AIStylesPage';
import AICostsPage from './costs/AICostsPage';
import WeightMatrixPage from './weights/WeightMatrixPage';
import ReasoningPromptsPage from './reasoning/ReasoningPromptsPage';
import GuardrailsPage from './guardrails/GuardrailsPage';
import InterviewBankPage from './interview/InterviewBankPage';
import CoverageDashboardPage from './coverage/CoverageDashboardPage';
import FidelityDashboardPage from './fidelity/FidelityDashboardPage';
import AgentsPage from './agents/AgentsPage';
import MirrorGuardianPage from './mirror-guardian/MirrorGuardianPage';
import OperationsPage from './operations/OperationsPage';
import DimensionsPage from './dimensions/DimensionsPage';
import FeatureFlagsPage from './flags/FeatureFlagsPage';
import VoiceConsentPage from './voice-consent/VoiceConsentPage';
import PhotoMemoriesPage from './photo-memories/PhotoMemoriesPage';

const START_COMMAND =
  'cd /Users/grant/LifeScribe/quill-app/services/quill-admin-backend && QUILL_INTERNAL_API_KEY=local-dev-quill-key npm start';

// In local dev the fix is "start the backend yourself"; in production it means
// the admin-backend proxy or the cross-VPC path to quill-admin-backend is down.
const IS_LOCAL_DEV =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname);

const DOWN_TITLE = IS_LOCAL_DEV
  ? 'Quill backend is not running'
  : 'Quill admin backend is unreachable';
const DOWN_DESCRIPTION = IS_LOCAL_DEV
  ? `Start it locally, then click Retry — ${START_COMMAND}`
  : 'The admin API could not reach quill-admin-backend (quill-production cluster). Check that the ECS service is healthy and the /api/admin/quill proxy route is deployed on admin-backend, then retry.';
const UNAUTHORIZED_DESCRIPTION = IS_LOCAL_DEV
  ? "The backend is running, but its QUILL_INTERNAL_API_KEY doesn't match the key the dev-server proxy is sending. Set QUILL_INTERNAL_API_KEY on the backend to match the one used by lifescribe-admin's webpack dev server, then retry."
  : "quill-admin-backend rejected the internal API key sent by the admin-backend proxy. The QUILL_INTERNAL_API_KEY values on the two task definitions don't match.";

const QuillSectionInner: React.FC = () => {
  const { status, recheck } = useQuillHealth();

  if (status === 'checking') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'down') {
    return (
      <EmptyState
        icon="error"
        title={DOWN_TITLE}
        description={DOWN_DESCRIPTION}
        action={{ label: 'Retry', onClick: recheck, variant: 'contained' }}
      />
    );
  }

  if (status === 'unauthorized') {
    return (
      <EmptyState
        icon="warning"
        title="Quill backend rejected the request"
        description={UNAUTHORIZED_DESCRIPTION}
        action={{ label: 'Retry', onClick: recheck, variant: 'contained' }}
      />
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="models" replace />} />
      <Route path="models" element={<AIModelsPage />} />
      <Route path="pipeline-models" element={<PipelineModelsPage />} />
      <Route path="ai-styles" element={<AIStylesPage />} />
      <Route path="costs" element={<AICostsPage />} />
      <Route path="weights" element={<WeightMatrixPage />} />
      <Route path="reasoning" element={<ReasoningPromptsPage />} />
      <Route path="guardrails" element={<GuardrailsPage />} />
      <Route path="interview" element={<InterviewBankPage />} />
      <Route path="coverage" element={<CoverageDashboardPage />} />
      <Route path="fidelity" element={<FidelityDashboardPage />} />
      <Route path="agents" element={<AgentsPage />} />
      <Route path="mirror-guardian" element={<MirrorGuardianPage />} />
      <Route path="operations" element={<OperationsPage />} />
      <Route path="dimensions" element={<DimensionsPage />} />
      <Route path="flags" element={<FeatureFlagsPage />} />
      <Route path="voice-consent" element={<VoiceConsentPage />} />
      <Route path="photo-memories" element={<PhotoMemoriesPage />} />
    </Routes>
  );
};

export const QuillSection: React.FC = () => {
  return (
    <QuillHealthProvider>
      <QuillSectionInner />
    </QuillHealthProvider>
  );
};

export default QuillSection;
